import { z } from 'zod'
import siteConfig from '../../../site.config.json'

/**
 * Client environment. Only VITE_-prefixed variables reach the browser, and Vite
 * inlines them as string literals at build time — so nothing secret goes here
 * (SPEC.md §14.2). Server-side secrets are Worker secrets.
 *
 * Validated at module load so a misconfigured build fails loudly rather than
 * shipping broken canonical URLs.
 */
/**
 * Default enquiry target: the Google Apps Script web app behind the contact
 * form. Public by design — the browser calls it directly, so it is not a
 * secret and belongs in a VITE_ variable.
 *
 * Note the deployment id changes every time the script is redeployed from the
 * Apps Script editor. When that happens this default goes stale and every
 * submission 404s, so override VITE_CONTACT_ENDPOINT rather than relying on it
 * long-term.
 */
const ENQUIRY_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbyccQW5SfY35qWYgVvSDDHDg_lVoZ1438vwHO6rWHXbC0D9c_vILwyp0fKxYF35CGUy/exec'

const envSchema = z.object({
  VITE_SITE_URL: z.string().url('VITE_SITE_URL must be an absolute URL'),
  VITE_CONTACT_ENDPOINT: z.string().min(1).default(ENQUIRY_ENDPOINT),
  VITE_TURNSTILE_SITE_KEY: z.string().optional(),
  VITE_ANALYTICS_DOMAIN: z.string().optional(),
  VITE_SENTRY_DSN: z.string().optional(),
})

function readEnv() {
  const parsed = envSchema.safeParse({
    /*
     * Falls back to the production origin, NOT to localhost.
     *
     * This used to default to http://localhost:5173 so `vite dev` worked with
     * no .env.local. The failure mode was severe and silent: any build where
     * VITE_SITE_URL was not set — CI, or a local `npm run build` without the
     * gitignored .env.production present — shipped localhost canonicals,
     * og:url and og:image to production. Search engines and link unfurlers got
     * an unreachable origin.
     *
     * Defaulting to production inverts that: the worst case is a dev server
     * emitting production canonicals, which nothing consumes. Point a dev or
     * staging build somewhere else by setting VITE_SITE_URL explicitly.
     */
    VITE_SITE_URL: import.meta.env.VITE_SITE_URL || siteConfig.siteUrl,
    VITE_CONTACT_ENDPOINT: import.meta.env.VITE_CONTACT_ENDPOINT || ENQUIRY_ENDPOINT,
    VITE_TURNSTILE_SITE_KEY: import.meta.env.VITE_TURNSTILE_SITE_KEY,
    VITE_ANALYTICS_DOMAIN: import.meta.env.VITE_ANALYTICS_DOMAIN,
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  })

  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`)
    throw new Error(`Invalid environment configuration:\n${issues.join('\n')}`)
  }

  return parsed.data
}

export const env = readEnv()

/** Absolute URL for a site-relative path. Used for canonicals, OG, sitemap. */
export function absoluteUrl(path: string): string {
  const base = env.VITE_SITE_URL.replace(/\/$/, '')
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${base}${suffix === '/' ? '' : suffix}` || base
}
